<template>
  <aside
    v-if="imageUrl"
    ref="slotCard"
    class="floating-slot-card"
    :class="[positionClass, {'floating-slot-card--ready': imageLoaded}]"
    :aria-label="cardLabel"
  >
    <a
      class="floating-slot-card__link"
      :href="openUrl"
      target="_blank"
      rel="noopener noreferrer"
      :aria-label="linkLabel"
    >
      <img
        class="floating-slot-card__image"
        :src="imageUrl"
        :alt="cardLabel"
        width="208"
        height="208"
        @load="handleImageLoad"
        @error="handleImageError"
      >
    </a>

    <button
      v-if="imageLoaded"
      type="button"
      class="floating-slot-card__close"
      aria-label="關閉功能推播圖卡"
      @click.stop.prevent="handleClose"
    >
      <v-icon size="20" color="grey darken-3">mdi-close</v-icon>
    </button>
  </aside>
</template>

<script>
import {mapGetters} from 'vuex'
import {
  buildSlotEventPayload,
  buildTrackedAssetUrl,
  buildTrackedOpenUrl,
  getSlotAssetUrl,
  getSlotContent,
  recordSlotEvent,
} from '@/api/slot-exposure'

const CLOSE_COOLDOWN_MS = 72 * 60 * 60 * 1000
const CLOSE_STORAGE_PREFIX = 'floating_slot_closed'

const SLOT_DEFINITIONS = [
  {
    routeName: 'home',
    slot: 'CRM首頁約診表左下方',
    page: 'CRM首頁',
    position: 'home',
  },
  {
    routeName: 'onlineReserve',
    slot: 'CRM線上約診頁面左下方',
    page: '線上約診',
    position: 'online-reserve',
  },
  {
    routeName: 'reschedule',
    slot: 'CRM改約頁面左下方',
    page: '改約',
    position: 'reschedule',
  },
  {
    routeName: 'review',
    slot: 'CRM評價總覽頁面左下方',
    page: '評價總覽',
    position: 'review',
  },
]

export default {
  name: 'FloatingSlotCard',

  data() {
    return {
      content: null,
      imageUrl: null,
      openUrl: null,
      imageLoaded: false,
      observer: null,
      requestSequence: 0,
      viewTracked: false,
    }
  },

  computed: {
    ...mapGetters([
      'axiosParamData',
      'liffUser',
    ]),

    slotDefinition() {
      const matchedRouteNames = this.$route.matched.map(route => route.name)
      return SLOT_DEFINITIONS.find(definition => matchedRouteNames.includes(definition.routeName)) || null
    },

    trackingIdentity() {
      const auth = this.axiosParamData || {}
      const viewer = auth.crm_user_id

      if (!viewer) return null

      if (auth.type === '1' && auth.clinic_code) {
        return {
          viewer: String(viewer),
          entityId: String(auth.clinic_code),
          idType: 'clinic',
        }
      }

      if (auth.type === '2' && auth.doctor_code) {
        return {
          viewer: String(viewer),
          entityId: String(auth.doctor_code),
          idType: 'doctor',
        }
      }

      return null
    },

    isDesktop() {
      return this.$vuetify.breakpoint.mdAndUp
    },

    slotContextKey() {
      if (!this.slotDefinition || !this.trackingIdentity || !this.isDesktop || this.liffUser) {
        return ''
      }

      const identity = this.trackingIdentity
      return [
        this.slotDefinition.slot,
        identity.viewer,
        identity.entityId,
        identity.idType,
      ].join('|')
    },

    positionClass() {
      return this.slotDefinition
        ? `floating-slot-card--${this.slotDefinition.position}`
        : ''
    },

    cardLabel() {
      return this.content && this.content.title
        ? this.content.title
        : '功能推播圖卡'
    },

    linkLabel() {
      if (this.content && this.content.cta_text) {
        return `${this.cardLabel}，${this.content.cta_text}`
      }
      return `${this.cardLabel}，開啟詳細內容`
    },
  },

  watch: {
    slotContextKey: {
      immediate: true,
      handler() {
        this.loadSlot()
      },
    },
  },

  beforeDestroy() {
    this.requestSequence += 1
    this.resetDisplay()
  },

  methods: {
    async loadSlot() {
      const requestId = ++this.requestSequence
      const contextKey = this.slotContextKey
      this.resetDisplay()

      if (!contextKey) return

      const definition = this.slotDefinition
      const tracking = this.createTracking(definition, this.trackingIdentity)

      try {
        const content = await getSlotContent(definition.slot)
        if (this.isStaleRequest(requestId, contextKey)) return
        if (!content || !content.version) return
        if (this.isClosed(definition.slot, content.version)) return

        const assetUrl = await getSlotAssetUrl(definition.slot, tracking.viewer)
        if (this.isStaleRequest(requestId, contextKey)) return
        if (!assetUrl) return

        this.content = content
        this.openUrl = buildTrackedOpenUrl(tracking)
        this.imageUrl = buildTrackedAssetUrl(assetUrl, tracking)
      } catch (error) {
        if (!this.isStaleRequest(requestId, contextKey)) {
          console.warn('[FloatingSlotCard] 無法載入功能推播圖卡', error)
          this.resetDisplay()
        }
      }
    },

    createTracking(definition, identity) {
      return {
        slot: definition.slot,
        page: definition.page,
        viewer: identity.viewer,
        entityId: identity.entityId,
        idType: identity.idType,
      }
    },

    isStaleRequest(requestId, contextKey) {
      return requestId !== this.requestSequence || contextKey !== this.slotContextKey
    },

    handleImageLoad() {
      this.imageLoaded = true
      this.$nextTick(() => this.observeView())
    },

    handleImageError() {
      this.requestSequence += 1
      this.resetDisplay()
    },

    observeView() {
      if (!this.$refs.slotCard || this.viewTracked) return

      if (typeof IntersectionObserver === 'undefined') {
        this.trackView()
        return
      }

      this.disconnectObserver()
      this.observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= 0.5)) {
          this.trackView()
        }
      }, {threshold: [0.5]})
      this.observer.observe(this.$refs.slotCard)
    },

    trackView() {
      if (this.viewTracked || !this.slotDefinition || !this.trackingIdentity) return

      this.viewTracked = true
      this.disconnectObserver()

      const tracking = this.createTracking(this.slotDefinition, this.trackingIdentity)
      const payload = buildSlotEventPayload(tracking, 'view')
      recordSlotEvent(payload).catch(error => {
        console.warn('[FloatingSlotCard] 無法記錄圖卡 view', error)
      })
    },

    handleClose() {
      if (!this.slotDefinition || !this.trackingIdentity || !this.content) return

      const definition = this.slotDefinition
      const tracking = this.createTracking(definition, this.trackingIdentity)
      const payload = buildSlotEventPayload(tracking, 'close')

      this.rememberClose(definition.slot, this.content.version)
      recordSlotEvent(payload).catch(error => {
        console.warn('[FloatingSlotCard] 無法記錄圖卡 close', error)
      })

      this.requestSequence += 1
      this.resetDisplay()
    },

    closeStorageKey(slot, version) {
      return `${CLOSE_STORAGE_PREFIX}:${slot}:${version}`
    },

    isClosed(slot, version) {
      const key = this.closeStorageKey(slot, version)

      try {
        const closedAt = Number(localStorage.getItem(key))
        if (!closedAt) return false

        const elapsed = Date.now() - closedAt
        if (elapsed >= 0 && elapsed < CLOSE_COOLDOWN_MS) return true

        localStorage.removeItem(key)
      } catch (error) {
        console.warn('[FloatingSlotCard] 無法讀取圖卡關閉狀態', error)
      }

      return false
    },

    rememberClose(slot, version) {
      try {
        localStorage.setItem(this.closeStorageKey(slot, version), String(Date.now()))
      } catch (error) {
        console.warn('[FloatingSlotCard] 無法保存圖卡關閉狀態', error)
      }
    },

    resetDisplay() {
      this.disconnectObserver()
      this.content = null
      this.imageUrl = null
      this.openUrl = null
      this.imageLoaded = false
      this.viewTracked = false
    },

    disconnectObserver() {
      if (!this.observer) return
      this.observer.disconnect()
      this.observer = null
    },
  },
}
</script>

<style scoped>
.floating-slot-card {
  position: fixed;
  left: 20px;
  bottom: 20px;
  width: 208px;
  height: 208px;
  z-index: 90;
  opacity: 0;
  pointer-events: none;
  transition: opacity 160ms ease;
}

.floating-slot-card--ready {
  opacity: 1;
  pointer-events: auto;
}

.floating-slot-card--home {
  left: 0;
  bottom: 76px;
}

.floating-slot-card--online-reserve {
  bottom: 52px;
}

.floating-slot-card--reschedule {
  bottom: 104px;
}

.floating-slot-card--review {
  left: 0;
  bottom: 0;
}

.floating-slot-card__link,
.floating-slot-card__image {
  display: block;
  width: 208px;
  height: 208px;
}

.floating-slot-card__image {
  object-fit: cover;
}

.floating-slot-card__close {
  position: absolute;
  top: 4px;
  right: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.24);
  cursor: pointer;
}

.floating-slot-card__close:focus-visible {
  outline: 3px solid var(--v-primary-base);
  outline-offset: 2px;
}

@media (max-width: 959px) {
  .floating-slot-card {
    display: none;
  }
}
</style>
